/**
 * Login screen — the only public route of the SPA.
 *
 * Behaviour worth knowing:
 *   - The initial `GET /api/auth/me` probe may still be running when someone
 *     opens /login directly. We show the spinner instead of the form, so a
 *     signed-in user does not see a login card flash before being redirected.
 *   - After a successful login the user returns to the location `RequireAuth`
 *     stored in `state.from` (a deep link that 401'd), never to /login itself.
 *   - Server messages are German and are rendered VERBATIM. `ApiError.detail`
 *     already carries the joined pydantic 422 array (`lib/api.ts`), so it is
 *     safe to print as a string — the raw body would render '[object Object]'.
 *   - The screen is a two-column split: the BrightFlare title image (with the
 *     white wordmark and the claim over it) on the left, the form on the right.
 *     Both collapse to a single column below 900px; the image is dropped
 *     entirely on a phone, where the card carries the star mark instead. All of
 *     that lives in section 12 of global.css.
 *   - 429: the throttle detail names the wait in seconds ("… bitte in 300
 *     Sekunden erneut versuchen"). We read that number back out of the message
 *     to disable the button and count down, because `ApiError` does not expose
 *     the `Retry-After` header. Failing to parse it only costs the countdown —
 *     the verbatim message is shown either way.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { BrandMark, BrandWordmark, ProductLogo } from '../components/BrandLogo';
import type { FromLocationState } from '../components/RequireAuth';
import Spinner, { PageSpinner } from '../components/Spinner';
import { ThemeToggle } from '../components/ThemeToggle';
import { ApiError, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';

/** Upper bound for the parsed lockout countdown — a sanity clamp, not a policy. */
const MAX_LOCKOUT_SECONDS = 3600;

/**
 * Recover the retry window from the 429 detail produced by
 * `LoginLockedError` in app/auth/service.py. Returns null when the message does
 * not carry a number, e.g. because it was customised.
 */
function lockoutSecondsFromDetail(detail: string): number | null {
  const match = /(\d{1,5})\s*Sekunden?/i.exec(detail);
  if (match === null) return null;
  const raw = match[1];
  if (raw === undefined) return null;
  const seconds = Number.parseInt(raw, 10);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.min(seconds, MAX_LOCKOUT_SECONDS);
}

/** Where to go after a successful login: the attempted location, or the root. */
function targetFrom(state: unknown): string {
  const from = (state as FromLocationState | null)?.from;
  if (from === undefined || typeof from.pathname !== 'string' || from.pathname === '') return '/';
  // Never bounce back into the login screen itself.
  if (from.pathname === '/login') return '/';
  return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
}

export function LoginPage(): ReactNode {
  const { user, loading, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [lockSeconds, setLockSeconds] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Tick the lockout countdown down to zero, then re-enable the form.
  useEffect(() => {
    if (lockSeconds <= 0) return;
    const timer = setInterval(() => {
      setLockSeconds((current) => (current <= 1 ? 0 : current - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [lockSeconds]);

  if (loading) return <PageSpinner text="Anmeldung wird geprüft…" />;
  if (user) return <Navigate to={targetFrom(location.state)} replace />;

  const locked = lockSeconds > 0;
  const canSubmit = !submitting && !locked && email.trim() !== '' && password !== '';

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await login({ email: email.trim(), password });
      // No navigation here: `user` becomes non-null and the <Navigate> above
      // takes over, which keeps the redirect logic in exactly one place.
    } catch (err) {
      if (!mounted.current) return;
      const detail = errorMessage(err);
      setError(detail);
      if (err instanceof ApiError && err.status === 429) {
        const seconds = lockoutSecondsFromDetail(detail);
        if (seconds !== null) setLockSeconds(seconds);
      }
      setPassword('');
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }

  return (
    <div className="login-screen">
      {/* The day/night switch has to exist here too: the login screen is the
          first thing a user sees, and the shell's switch is behind the session.
          The preference is stored locally, so the choice made here survives. */}
      <div className="login-theme">
        <ThemeToggle />
      </div>

      {/* Left: BrightFlare title image. Decorative — every word on it is real
          text above the photo, so nothing is lost when it is hidden on a phone. */}
      <section className="login-hero">
        <BrandWordmark tone="dark" alt="BrightFlare" />
        <div className="login-hero-copy">
          <h1 className="login-hero-title">
            Cybersecurity, <em>die niemals still steht.</em>
          </h1>
          <p className="login-hero-claim">
            AssetRadar hält fest, welche Produktversion bei welchem Kunden läuft, verfolgt neue
            Schwachstellen bis zur betroffenen Instanz und wacht über die Meldefristen des Cyber
            Resilience Act.
          </p>
          <ul className="login-hero-points">
            <li>CRA-Meldefristen</li>
            <li>KEV-Überwachung</li>
            <li>SBOM &amp; Auslieferungshistorie</li>
          </ul>
        </div>
      </section>

      {/* Right: the form. */}
      <section className="login-panel">
        <div className="login-card">
          <div className="login-brand">
            <ProductLogo alt="AssetRadar" />
          </div>
          <p className="login-sub">
            Melden Sie sich mit Ihrer Dienst-E-Mail an.
          </p>

          <form className="login-form" onSubmit={onSubmit} noValidate>
            <div className="field">
              <label htmlFor="login-email">E-Mail</label>
              <input
                id="login-email"
                type="email"
                name="email"
                autoComplete="username"
                autoFocus
                spellCheck={false}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={submitting || locked}
                aria-invalid={error !== null}
                required
              />
            </div>

            <div className="field">
              <label htmlFor="login-password">Passwort</label>
              <input
                id="login-password"
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={submitting || locked}
                aria-invalid={error !== null}
                required
              />
            </div>

            {error !== null ? (
              <div className="banner error" role="alert">
                {error}
                {locked ? (
                  <div className="banner-sub">
                    Erneuter Versuch in {lockSeconds} {lockSeconds === 1 ? 'Sekunde' : 'Sekunden'}
                    {' '}möglich.
                  </div>
                ) : null}
              </div>
            ) : null}

            <button type="submit" className="primary" disabled={!canSubmit}>
              {submitting ? (
                <>
                  <Spinner variant="on-accent" label="Anmeldung läuft…" /> Anmelden…
                </>
              ) : (
                'Anmelden'
              )}
            </button>
          </form>

          {import.meta.env.DEV ? (
            <div className="login-foot">
              <b>Entwicklungsmodus.</b> Der Seed-Administrator wird beim ersten Start angelegt:{' '}
              <code className="mono">CRA_ADMIN_EMAIL</code> aus <code className="mono">.env</code>{' '}
              (Standard <code className="mono">admin@cra-watch.local</code>) mit{' '}
              <code className="mono">CRA_ADMIN_PASSWORD</code>. Ist die Variable leer, wurde beim
              ersten Boot ein Zufallspasswort generiert und einmalig protokolliert:{' '}
              <code className="mono">docker compose logs cmdbuild_scanner | grep -i admin</code>
            </div>
          ) : null}

          <p className="login-legal">
            <BrandMark />
            BrightFlare · Cyber Resilience Act Monitoring
          </p>
        </div>
      </section>
    </div>
  );
}

export default LoginPage;
